import { Listbox } from "@headlessui/react";
import CheckIcon from "@mui/icons-material/Check";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import classNames from "classnames";
import React, {
  forwardRef,
  Fragment,
  Key,
  SelectHTMLAttributes,
  useMemo,
} from "react";
import Spinner from "ui/Spinner";

import Error from "../Error";
import Label from "../Label";

type Option = {
  value: SelectHTMLAttributes<any>["value"] | null;
  label: string;
};

export type Props = {
  name: string;
  options: Option[];
  value?: any;
  onChange?: (e: React.ChangeEvent<any>) => void;
  onBlur?: (e: React.FocusEvent<any>) => void;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean; // Nueva propiedad para control del loader
  errorMessage?: string;
  label?: string;
  disableOptionMap?: boolean;
};

const Select = forwardRef<HTMLButtonElement, Props>(
  (
    {
      name,
      options,
      value,
      onChange,
      placeholder = "Select...",
      disabled,
      isLoading = false,
      errorMessage,
      label,
      disableOptionMap = false,
    },
    ref,
  ) => {
    const optionMap = useMemo(
      () => new Map<Option["value"], Option>(options.map((o) => [o.value, o])),
      [options],
    );

    const selected = disableOptionMap ? value : optionMap.get(value);
    const selectedLabel = optionMap.get(value)?.label;

    const labelClasses = classNames("text-base flex items-center", {
      "text-gray-300": !selected && (disabled || isLoading),
      "text-gray-400": !selected && !(disabled || isLoading),
      "text-white": selected,
      "flex justify-center": isLoading,
    });

    return (
      <div className="flex flex-col mb-4 relative">
        {label && <Label htmlFor={name} text={label} />}
        <Listbox
          value={selected}
          disabled={disabled || isLoading}
          onChange={(value) =>
            onChange &&
            onChange({
              target: { name: name, id: name, value: value?.value },
            } as React.ChangeEvent<any>)
          }
        >
          <Listbox.Button
            name={name}
            id={name}
            ref={ref}
            className={classNames(
              "relative py-2 pl-3 w-full bg-faux-police-blue rounded border text-sm text-left focus:outline-none leading-none",
              disabled || isLoading
                ? `opacity-50 bg-gray-500 text-gray-500 border-gray-400 cursor-not-allowed ${isLoading && "pl-0"}`
                : "border-white focus:ring focus:border-blue-500",
            )}
          >
            <span className={labelClasses}>
              {isLoading ? (
                <Spinner color="white" size={20} />
              ) : (
                selected?.label || selectedLabel || placeholder
              )}
            </span>
            {!isLoading && (
              <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <UnfoldMoreIcon
                  className="text-faux-whirlpool"
                  fontSize="small"
                />
              </span>
            )}
          </Listbox.Button>
          {!isLoading && (
            <Listbox.Options className="absolute w-full top-10 z-10 bg-faux-police-blue rounded-md overflow-y-scroll mt-1 space-y-1 shadow-sm border border-gray-300 focus:outline-none">
              {options.map((option) => (
                <Listbox.Option
                  key={option.value as Key}
                  value={option}
                  as={Fragment}
                >
                  {({ active, selected }) => (
                    <div
                      className={classNames(
                        "flex flex-row items-center cursor-pointer text-white text-sm px-3 py-2 w-full hover:bg-faux-ultramarine-blue hover:bg-opacity-40",
                        selected && "font-bold",
                        active && "bg-faux-ultramarine-blue bg-opacity-10",
                      )}
                    >
                      <p>{option.label}</p>
                      {selected && (
                        <CheckIcon
                          className="pl-1 text-faux-ultramarine-blue"
                          fontSize="medium"
                        />
                      )}
                    </div>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          )}
        </Listbox>
        {errorMessage && <Error text={errorMessage} />}
      </div>
    );
  },
);

Select.displayName = "Select";

export default Select;
